/**
 * MessageBubble Component (Shared)
 *
 * Renders individual message bubbles in Slack-style chat.
 * Supports text, images, files, system messages, edited/deleted states,
 * and reply-to thread previews.
 *
 * #449: adds an inline "Editar" flow (kebab menu -> textarea -> Guardar),
 * gated to own messages within the same 15-minute window the backend
 * (`chatService.editMessage`) already enforces — the backend supported
 * editing since before this issue; only the UI entry point was missing.
 *
 * @module components/chat/MessageBubble
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Dropdown } from 'react-bootstrap';
import { CHAT_FILE_LABELS } from '../../utils/constants';

const EDIT_WINDOW_MINUTES = 15;

/**
 * Human-readable attachment size (#451). Kept local rather than imported from
 * ChatInput so the bubble does not depend on the composer.
 */
const formatAttachmentSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  return format(date, 'HH:mm');
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Exported for tests — mirrors the 15-minute window `chatService.editMessage`
 * enforces server-side, so the "Editar" menu item never shows a false
 * affordance the backend will just reject.
 */
export const isWithinEditWindow = (createdAt, now = Date.now()) => {
  if (!createdAt) return false;
  const minutesSinceCreation = (now - new Date(createdAt).getTime()) / 60000;
  return minutesSinceCreation <= EDIT_WINDOW_MINUTES;
};

const CheckIcon = ({ double = false, read = false }) => (
  <svg viewBox="0 0 16 11" height="11" width="16" preserveAspectRatio="xMidYMid meet">
    {double ? (
      <>
        <path
          d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .241.178.661.661 0 0 0 .304.063.528.528 0 0 0 .39-.178l6.778-8.366a.499.499 0 0 0 .102-.318.499.499 0 0 0-.102-.317l-.305-.305z"
          fill={read ? '#53bdeb' : '#8696a0'}
        />
        <path
          d="M15.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-1.152-1.089a.251.251 0 0 1 .178-.432h.001a.251.251 0 0 1 .177.074l.97.916 6.19-7.636a.499.499 0 0 0 .102-.318.499.499 0 0 0-.102-.317l-.305-.305a.457.457 0 0 0-.184-.105z"
          fill={read ? '#53bdeb' : '#8696a0'}
        />
      </>
    ) : (
      <path
        d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .241.178.661.661 0 0 0 .304.063.528.528 0 0 0 .39-.178l6.778-8.366a.499.499 0 0 0 .102-.318.499.499 0 0 0-.102-.317l-.305-.305z"
        fill="#8696a0"
      />
    )}
  </svg>
);

/**
 * MessageBubble Component
 */
const MessageBubble = ({
  message,
  isOwn = false,
  isRead = false,
  showAvatar = true,
  showName = true,
  onImageClick,
  onReply,
  onShareToBitacora,
  onDelete,
  onEdit,
  canShareToBitacora = false,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content || '');
  const [savingEdit, setSavingEdit] = useState(false);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [isEditing]);

  const startEdit = () => {
    setEditValue(message.content || '');
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditValue(message.content || '');
  };

  const saveEdit = async () => {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === message.content) {
      setIsEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      await onEdit(message, trimmed);
      setIsEditing(false);
    } catch {
      // Parent (ChatView/MessagesPage) already surfaces a toast on failure —
      // keep the editor open so the user can retry without retyping.
    } finally {
      setSavingEdit(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // System messages render differently
  if (message.message_type === 'system') {
    return (
      <div className="message-system">
        <span className="message-system-content">
          {message.content}
        </span>
      </div>
    );
  }

  // Deleted message placeholder
  if (message.is_deleted) {
    return (
      <div className={`message-wrapper ${isOwn ? 'own' : 'other'}`}>
        <div className={`message-bubble ${isOwn ? 'own' : 'other'} message-deleted`}>
          <span className="message-deleted-text">
            <i className="bi bi-slash-circle me-1" />
            Este mensaje fue eliminado
          </span>
          <span className="message-footer">
            <span className="message-time">
              {formatMessageTime(message.created_at)}
            </span>
          </span>
        </div>
      </div>
    );
  }

  // Check for image attachments
  const attachments = message.metadata?.attachment_urls || [];
  const parsedAttachments = typeof message.attachments === 'string'
    ? JSON.parse(message.attachments || '[]')
    : (message.attachments || []);
  const allAttachments = [...attachments, ...parsedAttachments];
  const imageAttachment = allAttachments.find(
    a => typeof a === 'string' || a?.type?.startsWith('image/')
  );
  const imageUrl = typeof imageAttachment === 'string'
    ? imageAttachment
    : imageAttachment?.url || null;
  const hasImage = !!imageUrl;

  // #451: non-image attachments (PDF, Word, Excel, ...). A document cannot be
  // rendered in an <img>, so it gets an icon + filename + download link. Bare
  // URL strings are treated as images by the finder above (legacy shape), so
  // only object-shaped attachments with a non-image `type` land here.
  const fileAttachments = allAttachments.filter(
    a => a && typeof a === 'object' && a.url && !a.type?.startsWith('image/')
  );

  // `chat_messages` has no per-recipient `is_read` column — read state is
  // derived by the parent (MessageList) from `chat_participants.last_read_at`
  // and passed down as `isRead`. See #446.
  const isEdited = message.is_edited === true;

  // Reply preview
  const replyTo = message.reply_to;

  // Per-message kebab actions
  const canDelete = isOwn && !!onDelete;
  const canShare = canShareToBitacora && !!onShareToBitacora;
  // #449: edit is own-message + within the same 15-minute window the
  // backend enforces (see isWithinEditWindow above).
  const canEdit = isOwn && !!onEdit && isWithinEditWindow(message.created_at);
  const hasActions = !!onReply || canShare || canDelete || canEdit;
  const sharedToBitacoraAt = message.metadata?.shared_to_bitacora_at;

  const renderContent = () => (
    <div className={`message-bubble ${isOwn ? 'own' : 'other'}`}>
      {/* Per-message kebab menu (reply / share to bitacora / delete) */}
      {hasActions && (
        <div className="message-kebab">
          <Dropdown align="end">
            <Dropdown.Toggle
              as="button"
              className="message-kebab-btn"
              bsPrefix="message-kebab-toggle"
              aria-label="Opciones del mensaje"
            >
              <i className="bi bi-three-dots-vertical" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {onReply && (
                <Dropdown.Item onClick={() => onReply(message)}>
                  <i className="bi bi-reply me-2" />
                  Responder
                </Dropdown.Item>
              )}
              {canEdit && (
                <Dropdown.Item onClick={startEdit}>
                  <i className="bi bi-pencil me-2" />
                  Editar
                </Dropdown.Item>
              )}
              {canShare && (
                <Dropdown.Item onClick={() => onShareToBitacora(message)}>
                  <i className="bi bi-journal-bookmark me-2" />
                  Compartir en bitácora
                </Dropdown.Item>
              )}
              {canDelete && (
                <Dropdown.Item
                  onClick={() => onDelete(message)}
                  className="text-danger"
                >
                  <i className="bi bi-trash me-2" />
                  Eliminar
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      )}

      {/* Sender name for other's messages */}
      {!isOwn && showName && message.sender && (
        <div className="message-sender">
          {message.sender.full_name || 'Usuario'}
        </div>
      )}

      {/* Reply quote preview */}
      {replyTo && (
        <div className="reply-quote">
          <span className="reply-quote-sender">
            {replyTo.sender?.full_name || 'Usuario'}
          </span>
          <span className="reply-quote-text">
            {replyTo.content?.substring(0, 100) || ''}
            {replyTo.content?.length > 100 ? '...' : ''}
          </span>
        </div>
      )}

      {/* Image attachment */}
      {hasImage && !imageError && (
        <div className="message-image-container">
          {!imageLoaded && (
            <div className="message-image-loading">
              <div className="message-loading-spinner" />
            </div>
          )}
          <img
            src={imageUrl}
            alt="Adjunto"
            className="message-image"
            style={{ display: imageLoaded ? 'block' : 'none' }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            onClick={() => onImageClick && onImageClick(imageUrl)}
          />
        </div>
      )}

      {/* File attachments (non-image) — icon + name + download link (#451) */}
      {fileAttachments.map((file, idx) => {
        const meta = CHAT_FILE_LABELS[file.type];
        const label = [meta?.label, formatAttachmentSize(file.size)]
          .filter(Boolean)
          .join(' · ');
        return (
          <a
            key={file.url || idx}
            className="message-file-attachment"
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            download={file.name || undefined}
            aria-label={`Descargar ${file.name || 'archivo adjunto'}`}
          >
            <i className={`bi ${meta?.icon || 'bi-file-earmark'} message-file-icon`} aria-hidden="true" />
            <span className="message-file-info">
              <span className="message-file-name">{file.name || 'Archivo adjunto'}</span>
              {label && <span className="message-file-meta">{label}</span>}
            </span>
            <i className="bi bi-download message-file-download" aria-hidden="true" />
          </a>
        );
      })}

      {/* Legacy `file` messages that carry no structured attachment */}
      {message.message_type === 'file' && !hasImage && fileAttachments.length === 0 && (
        <div className="message-file-attachment">
          <i className="bi bi-file-earmark message-file-icon" aria-hidden="true" />
          <span className="message-file-info">
            <span className="message-file-name">{message.content || 'Archivo adjunto'}</span>
          </span>
        </div>
      )}

      {/* Inline edit mode (#449) or plain message text */}
      {isEditing ? (
        <div className="message-edit-container">
          <textarea
            ref={editInputRef}
            className="message-edit-textarea"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleEditKeyDown}
            disabled={savingEdit}
            rows={2}
            aria-label="Editar mensaje"
          />
          <div className="message-edit-actions">
            <button
              type="button"
              className="message-edit-btn message-edit-cancel"
              onClick={cancelEdit}
              disabled={savingEdit}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="message-edit-btn message-edit-save"
              onClick={saveEdit}
              disabled={savingEdit || !editValue.trim()}
            >
              {savingEdit ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      ) : (
        // A `file` message with a structured attachment shows its content as a
        // caption; the legacy shape (no attachment) puts the filename in
        // `content`, which the block above already renders (#451).
        message.content && (message.message_type !== 'file' || fileAttachments.length > 0) && (
          <span className="message-content">{message.content}</span>
        )
      )}

      {/* "Shared to bitacora" badge */}
      {sharedToBitacoraAt && (
        <div className="bitacora-badge">
          <i className="bi bi-journal-check" />
          En bitácora
        </div>
      )}

      {/* Message footer with time, edited indicator, and status */}
      <span className="message-footer">
        {isEdited && (
          <span className="message-edited-indicator">(editado)</span>
        )}
        <span className="message-time">
          {formatMessageTime(message.created_at)}
        </span>
        {isOwn && (
          <span className={`message-status ${isRead ? 'read' : 'sent'}`}>
            <CheckIcon double={true} read={isRead} />
          </span>
        )}
      </span>
    </div>
  );

  return (
    <div
      id={`chat-message-${message.id}`}
      className={`message-wrapper ${isOwn ? 'own' : 'other'}`}
    >
      {!isOwn && showAvatar ? (
        <div className="message-with-avatar">
          <div className="message-avatar">
            {message.sender?.avatar_url ? (
              <img src={message.sender.avatar_url} alt={message.sender.full_name} />
            ) : (
              getInitials(message.sender?.full_name)
            )}
          </div>
          {renderContent()}
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    message_type: PropTypes.oneOf(['text', 'image', 'file', 'system']),
    is_deleted: PropTypes.bool,
    is_edited: PropTypes.bool,
    metadata: PropTypes.object,
    attachments: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    reply_to: PropTypes.shape({
      id: PropTypes.string,
      content: PropTypes.string,
      sender: PropTypes.shape({
        id: PropTypes.string,
        full_name: PropTypes.string,
      }),
    }),
    sender: PropTypes.shape({
      id: PropTypes.string,
      full_name: PropTypes.string,
      avatar_url: PropTypes.string,
    }),
  }).isRequired,
  isOwn: PropTypes.bool,
  isRead: PropTypes.bool,
  showAvatar: PropTypes.bool,
  showName: PropTypes.bool,
  onImageClick: PropTypes.func,
  onReply: PropTypes.func,
  onShareToBitacora: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  canShareToBitacora: PropTypes.bool,
};

export default MessageBubble;
